import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Modal from "../../components/ui-components/Modal";
import Badge from "../../components/ui-components/Badge";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import { toast } from "../../components/ui-components/Toast";
import { ControlCheckFindingsList } from "./controlCheckFindings";
import { useStarterSetMutation } from "./api";
import { friendlyRoleError } from "./roleFormUtils";

// Sub-project E4, Task 5 — the guided finance starter set. Opened from the
// Templates tab's "Create recommended finance role set" action
// (RolesCatalogPage). Two steps:
//
// 1. Confirm: explains what's about to happen (draft finance roles,
//    control-checked, nothing assigned to any user) before the tenant admin
//    commits to anything.
// 2. Results: once POST /roles/starter-set returns, lists each role the
//    call resolved — newly created or already `existing` (the endpoint is
//    idempotent per template) — with its own control-check summary (the
//    same ControlCheckFindingsList OrgRoleWizard/TemplateDiff use) and a
//    link to that role's own page to finish reviewing it and activate it.
//    Nothing here assigns a user or activates a role on the admin's behalf.
export default function StarterSetPanel({ open, onClose }) {
  const navigate = useNavigate();

  const mutation = useStarterSetMutation({
    onError: (err) => {
      toast.error(friendlyRoleError(err, "create the starter role set"));
    },
  });

  const drafts = mutation.data;
  const hasResult = mutation.isSuccess && Array.isArray(drafts);

  const handleClose = () => {
    mutation.reset();
    onClose();
  };

  const handleConfirm = () => {
    mutation.mutate({ set: "finance" });
  };

  const handleOpenDraft = (role) => {
    handleClose();
    navigate(`/settings/roles/${role.id}`);
  };

  return (
    <Modal
      show={open}
      close={handleClose}
      title="Create recommended finance role set"
      icon={icon({ name: "wand-magic-sparkles", style: "solid" })}
      cancelButton=""
      confirmButton=""
      size="lg"
    >
      {!hasResult && (
        <>
          <p className="text-sm text-text-2">
            This drafts a starter set of finance role templates — Finance Manager, Budget Owner, and
            similar least-privilege personas — as{" "}
            <span className="font-medium text-text">organisation roles in DRAFT status</span>, each
            passed through the same control-check activation would run. Nothing is assigned to any
            user, and no role is activated — every draft still needs to be opened and reviewed before
            it's ready to assign.
          </p>
          {mutation.isError && (
            <p className="mt-3 text-sm text-danger" data-testid="starter-set-error">
              Couldn't create the starter role set — try again.
            </p>
          )}
          <div className="flex items-center gap-2.5 mt-5 pt-4 border-t border-border">
            <button type="button" className={secondaryButtonCls} onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className={primaryButtonCls}
              onClick={handleConfirm}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Creating…" : "Create draft roles"}
            </button>
          </div>
        </>
      )}

      {hasResult && (
        <>
          <div className="flex items-center gap-2 text-sm text-success mb-3">
            <FontAwesomeIcon icon={icon({ name: "circle-check", style: "solid" })} />
            {drafts.length} finance role{drafts.length === 1 ? "" : "s"} ready for review — none assigned
            to any user.
          </div>

          <div className="divide-y divide-border" data-testid="starter-set-results">
            {drafts.map((r) => (
              <div key={r.id} className="py-3" data-testid={`starter-set-role-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-text">{r.name}</div>
                    {r.templateCode && <div className="text-xs text-text-2 mt-0.5">{r.templateCode}</div>}
                  </div>
                  <Badge
                    status={r.existing ? "neutral" : "success"}
                    label={r.existing ? "Already exists" : "Newly created"}
                    size="sm"
                  />
                </div>

                <div className="mt-2">
                  <ControlCheckFindingsList findings={r.findings || []} />
                </div>

                <button
                  type="button"
                  onClick={() => handleOpenDraft(r)}
                  className="mt-2 inline-flex items-center gap-1 border-0 box-border bg-transparent p-0 text-xs font-medium text-primary hover:text-primary-hover cursor-pointer"
                >
                  Open draft to finish &amp; activate
                  <FontAwesomeIcon icon={icon({ name: "arrow-right", style: "solid" })} className="text-[10px]" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2.5 mt-5 pt-4 border-t border-border">
            <button type="button" className={primaryButtonCls} onClick={handleClose}>
              Done
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

StarterSetPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
